import { AccordionCard } from 'components/AccordionCard/AccordionCard'
import React from 'react'
import { Box, Flex, Message, Text, useAppTheme } from '@workwhile/ui'
import { useBookingDetailsContextValue } from '../context/BookingDetailsProvider'
import { LinkShiftForm } from './LinkShiftForm'
import { formatInTimeZone } from 'date-fns-tz'
import { DAYFORCE_SHIFTS_DEFAULT_TIMEZONE } from 'pages/Dayforce/constants'
import { useDayforceJobContextValue } from 'pages/Dayforce/shared/context/DayforceJobProvider'
import { BookingShiftDetail } from './BookingShiftDetail/BookingShiftDetail'

export const BookingDetailsMain = () => {
  const { space } = useAppTheme()

  const {
    state: { bookingIdx, dayforceBooking },
  } = useBookingDetailsContextValue()

  const {
    state: { data: dayforceJob },
  } = useDayforceJobContextValue()

  const startsAt = formatInTimeZone(
    dayforceBooking.startsAt,
    DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
    'EEEE, MMMM d, h:mm a z'
  )
  const finishesAt = formatInTimeZone(
    dayforceBooking.finishesAt,
    DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
    'EEEE, MMMM d, h:mm a z'
  )

  const noWorkersRequired = dayforceBooking.workersRequired === 0

  return (
    <Flex flexDirection="row" gap={3} id={dayforceBooking.bookingId}>
      <Box style={{ width: '70%' }}>
        <AccordionCard
          title={
            <Text fontWeight="bold">
              {bookingIdx + 1}/{dayforceJob.bookings.length} - Booking details
              from Dayforce
            </Text>
          }
          size="large"
          contentWrapperProps={{
            display: 'flex',
            flexDirection: 'column',
            gap: space[2],
          }}
        >
          {noWorkersRequired ? (
            <Message variant="warning" borderRadius="small">
              No workers required for this booking. This is likely a canceled
              job.
            </Message>
          ) : null}
          <Text>
            <Text as="span" fontWeight="bold">
              Booking ID:{' '}
            </Text>
            {dayforceBooking.bookingId}
          </Text>
          {dayforceBooking.hazmatOldBookingId ? (
            <Text>
              <Text as="span" fontWeight="bold">
                "Old" Booking ID:{' '}
              </Text>
              {dayforceBooking.hazmatOldBookingId}
            </Text>
          ) : null}
          <Text>
            <Text as="span" fontWeight="bold">
              Job Name:{' '}
            </Text>
            {dayforceBooking.jobName || ''}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Position ID:{' '}
            </Text>
            {dayforceBooking.position.id}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Position Name:{' '}
            </Text>
            {dayforceBooking.position.name} ({dayforceBooking.level.name})
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Starts At:{' '}
            </Text>
            {startsAt}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Finishes At:{' '}
            </Text>
            {finishesAt}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Workers Assigned:{' '}
            </Text>
            {dayforceBooking.workersAssigned}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Workers Required:{' '}
            </Text>
            {dayforceBooking.workersRequired}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Backup Workers Assigned:{' '}
            </Text>
            {dayforceBooking.backupWorkersAssigned}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Backup Workers Required:{' '}
            </Text>
            {dayforceBooking.backupWorkersRequired}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Bill Rate:{' '}
            </Text>
            {dayforceBooking.billRate}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Pay Rate:{' '}
            </Text>
            {dayforceBooking.payRate}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Bonus:{' '}
            </Text>
            {dayforceBooking.bonus}
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Travel Stipend:{' '}
            </Text>
            {dayforceBooking.travelStipend}
          </Text>
          <Flex flexDirection="column" gap={space[1]}>
            <Text fontWeight="bold">Dress Code:</Text>
            <Box
              as="pre"
              margin={0}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {dayforceBooking.dressCode}
            </Box>
          </Flex>
          <Flex flexDirection="column" gap={space[1]}>
            <Text fontWeight="bold">Special Instructions:</Text>
            <Box
              as="pre"
              margin={0}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {dayforceBooking.specialInstructions}
            </Box>
          </Flex>
          <Flex flexDirection="column" gap={space[1]}>
            <Text fontWeight="bold">Additional Tasks:</Text>
            <Box
              as="pre"
              margin={0}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
              }}
            >
              {dayforceBooking.additionalTasks}
            </Box>
          </Flex>
          <Text fontWeight="bold">Managers:</Text>
          <Flex flexDirection="column" gap={space[1]}>
            {dayforceJob.managers.map((manager) => (
              <Flex
                key={manager.id}
                flexDirection="column"
                gap={space[1]}
                marginLeft={space[2]}
              >
                <Text fontWeight="bold">
                  - {manager.firstName} {manager.lastName}
                </Text>
                <Text paddingLeft="1rem">
                  <Text as="span" fontWeight="bold">
                    Email:{' '}
                  </Text>
                  {manager.email}
                </Text>
                <Text paddingLeft="1rem">
                  <Text as="span" fontWeight="bold">
                    Phone:{' '}
                  </Text>
                  {manager.phoneNumber}
                </Text>
                <Text paddingLeft="1rem">
                  <Text as="span" fontWeight="bold">
                    Primary Contact:{' '}
                  </Text>
                  {manager.primaryContact ? 'Yes' : 'No'}
                </Text>
                <Text paddingLeft="1rem">
                  <Text as="span" fontWeight="bold">
                    Booker:{' '}
                  </Text>
                  {manager.booker ? 'Yes' : 'No'}
                </Text>
              </Flex>
            ))}
          </Flex>
        </AccordionCard>
      </Box>
      {dayforceBooking.shifts.length > 0 ? (
        <AccordionCard
          title={
            <Flex
              flexDirection="row"
              gap={space[2]}
              justifyContent="space-between"
              width="100%"
            >
              <Text color="lightText">Booking linked to WorkWhile shifts</Text>
            </Flex>
          }
          size="large"
          contentWrapperProps={{
            display: 'flex',
            flexDirection: 'column',
            gap: space[2],
          }}
        >
          {dayforceBooking.shifts.map((shift, index) => (
            <BookingShiftDetail
              key={shift.id}
              shiftId={shift.id}
              shiftIdx={index}
            />
          ))}
        </AccordionCard>
      ) : (
        <AccordionCard
          title="No shift linked to this booking"
          size="large"
          contentWrapperProps={{
            display: 'flex',
            flexDirection: 'column',
            gap: space[3],
            alignItems: 'flex-start',
          }}
        >
          <LinkShiftForm />
        </AccordionCard>
      )}
    </Flex>
  )
}
